import {
  HEADER_PILL,
  HEADER_PILL_ACTIVE,
  HEADER_PILL_IDLE,
} from '@/shared/components/pillClasses';

import { SIGNAL_STATUSES, signalState } from '../signalStatus';

export interface SignalStatusFilterProps {
  /** The status being shown, or `undefined` for all of them. */
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** How many signals carry each status, for the pill's own count. */
  counts: Record<string, number>;
}

/**
 * Narrow the signal list to one status (spec 090 FR-008).
 *
 * Built here rather than borrowed from `TaskFilterRow`, which is exported and is
 * genuinely shared across three surfaces. What stops it being a fourth is domain
 * language (constitution §3): its vocabulary is board states and task labels, and
 * a signal has neither. Reusing it would mean generalizing a task-shaped component
 * to take an arbitrary status vocabulary — at which point it no longer belongs in
 * `features/tasks` at all, and three surfaces would be refactored to serve one.
 *
 * What *is* shared is the part that should be: `pillClasses` already lives in
 * `shared/`, so the pills look like the product's other pills without this feature
 * reaching into another one. FR-008 asked for the pattern, and the pattern is a
 * stylesheet, not a component.
 */
export function SignalStatusFilter({
  value,
  onChange,
  counts,
}: SignalStatusFilterProps) {
  return (
    <ul className="flex flex-wrap items-center gap-2">
      {SIGNAL_STATUSES.map((status) => {
        const active = value === status;
        const count = counts[status] ?? 0;
        return (
          <li key={status}>
            <button
              type="button"
              aria-pressed={active}
              onClick={() => {
                // Pressing the active filter again is how someone gets back out
                // of it, so the same control both sets and clears — the rule the
                // task surfaces already established.
                onChange(active ? undefined : status);
              }}
              className={`${HEADER_PILL} ${
                active ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE
              }`}
            >
              {signalState(status).label}
              <span className="tabular-nums opacity-70">{count}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
